import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../state/useSession';
import { newAppPath } from '../state/builderMode';
import { useProjects } from '../state/projects';
import { agentConfigured, loadStudio, siteUrl, type AgentSite } from '../api/agent';
import { isUnfinished, isWebApp } from '@/webapp/webAppRecord';
import { siteLink } from '@/webapp/siteAddress';
import { timeAgo } from '@/utils/time';
import type { AppDefinition } from '@/types/app';
import type { Project } from '@/storage/ProjectRepository';
import { EmptyState, FAB, Screen } from '../ui';
import ProjectCard from './ProjectCard';

type Row = { kind: 'project'; at: string; project: Project } | { kind: 'agent'; at: string; site: AgentSite };

/**
 * Ported from src/app/(main)/(tabs)/index.tsx — the projects you are building.
 *
 * Sites made in the ⚡ agent builder are listed here too, newest first among the rest (owner,
 * 2026-11-04: "They should show up without the studio, just in the build page"). An account
 * without the agent builder gets a refusal from the studio list, which simply adds nothing.
 */
export default function Home() {
  const navigate = useNavigate();
  const { user } = useSession();
  const projects = useProjects((s) => s.projects);
  const isLoading = useProjects((s) => s.isLoading);
  const [agentSites, setAgentSites] = useState<AgentSite[]>([]);

  useEffect(() => {
    void useProjects.getState().loadProjects(user?.id ?? '');
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !agentConfigured()) return;
    let live = true;
    loadStudio()
      .then((s) => { if (live) setAgentSites(s.sites); })
      .catch(() => { if (live) setAgentSites([]); });
    return () => { live = false; };
  }, [user?.id]);

  const rows: Row[] = [
    ...projects.map((project): Row => ({ kind: 'project', at: project.updated_at, project })),
    ...agentSites.map((site): Row => ({ kind: 'agent', at: site.updatedAt, site })),
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));

  return (
    <Screen title="Build">
      {rows.length === 0 && !isLoading ? (
        <EmptyState
          icon="emptyApps"
          title="No projects yet"
          body="Describe an app in a sentence and Saddle builds it."
          ctaTitle="Create an app"
          onCta={() => navigate(newAppPath())}
        />
      ) : (
        <ul className="project-list app-grid">
          {rows.map((row) => {
            if (row.kind === 'agent') {
              const s = row.site;
              const name = s.title || 'Untitled site';
              const url = s.slug ? siteUrl(s.slug) : null;
              return (
                <ProjectCard
                  key={`agent-${s.siteId}`}
                  name={name}
                  onOpenEditor={() => navigate(`/studio/sites/${s.siteId}`)}
                  href={url}
                  address={url?.replace('https://', '')}
                  meta={s.slug ? `⚡ Agent · ${timeAgo(s.updatedAt)}` : '⚡ Agent · Building…'}
                />
              );
            }
            const p = row.project;
            const definition = p.definition as AppDefinition;
            // Where the page lives; null for an app with no page, or a build that did not land.
            const site = siteLink(definition);
            const unfinished = isUnfinished(definition);
            return (
              <ProjectCard
                key={p.id}
                name={p.name}
                onOpenEditor={() => navigate(`/project/${p.id}`)}
                href={site?.url}
                address={site?.address}
                meta={
                  /* A build that did not land keeps its row; this says why it is there. */
                  unfinished ? (
                    <span className="project-warn">Not built yet</span>
                  ) : !isWebApp(definition) ? (
                    `Phone only · ${timeAgo(p.updated_at)}`
                  ) : (
                    timeAgo(p.updated_at)
                  )
                }
              />
            );
          })}
        </ul>
      )}
      <FAB icon="add" onPress={() => navigate(newAppPath())} accessibilityLabel="New app" />
    </Screen>
  );
}
